import { type SqlString, sql } from "../SqlString";
import { type SqliteTableDef } from "../schema";
import { type SqliteTableInfo } from "./runGetTableXInfo";

/**
 * Handles the data migration side of a hard SQLite schema migration. SQLite does not support
 * in-place column upgrades in most cases, so we need to migrate the data to the new table for each
 * column upgrade.
 */
export function migrateDataSql(
	currentTable: SqliteTableInfo,
	desiredTable: SqliteTableDef,
	options?: { oldColumnName?: string; newColumnName?: string }
): SqlString {
	const sourceTable = currentTable.name;
	const targetTable = desiredTable.tableName;

	// Build the SELECT clause
	const selectClause = sql.join(
		desiredTable.columns.map((col): SqlString => {
			// If we're renaming a column and this is the new column name, use the old column name
			if (options?.oldColumnName && options?.newColumnName) {
				// If this is the new column name, use the old column name
				if (col.name === options.newColumnName) {
					return sql.quote(options.oldColumnName);
				}
				// If this is the old column name, skip it since it's being renamed
				if (col.name === options.oldColumnName) {
					return sql.lit("NULL");
				}
			}

			const sourceColumn = currentTable.columns.find((c) => c.name === col.name);
			if (sourceColumn) {
				return sql.quote(sourceColumn.name);
			}

			// Find the column definition in the table definition.
			//
			// It's not accidentally quadratic if it's on purpose!
			const columnDef = desiredTable.columns.find((c) => c.name === col.name);
			if (!columnDef) {
				throw new Error(`Column definition not found for ${col}`);
			}

			// If column is nullable, use NULL
			if (columnDef.nullable !== false) {
				return sql.lit("NULL");
			}

			// For non-nullable columns, use the default value or throw
			if (columnDef.defaultValue === undefined) {
				throw new Error(`Non-nullable column ${col.name} has no default value`);
			}

			// Format the default value appropriately
			if (columnDef.defaultValue === null || columnDef.defaultValue === undefined) {
				return sql.lit("NULL");
			}
			if (typeof columnDef.defaultValue === "string") {
				return sql.quote(columnDef.defaultValue);
			}
			if (
				typeof columnDef.defaultValue === "number" ||
				typeof columnDef.defaultValue === "boolean"
			) {
				return sql.lit(columnDef.defaultValue);
			}
			return columnDef.defaultValue;
		}),
		",\n	"
	);

	return sql`INSERT INTO ${sql.quote(targetTable)} SELECT
	${selectClause}
FROM ${sql.quote(sourceTable)}`;
}
